import { GameStatuses } from 'src/Logic/Models/General/gameStatuses';
import { AudienceParticipantDataType } from 'src/Logic/Models/Response/audience/gameStatus';
import { ToDisplayNumber } from 'src/Logic/Helpers/number-helpers';
import { TableContainer } from './styles';

type Props = {
    participants: AudienceParticipantDataType[] | undefined;
    gameStatus: GameStatuses;
};

const getScaleFactor = (playerCount: number): number => {
    if (playerCount <= 4) return 1.5;
    if (playerCount <= 7) return 1.125;
    return 0.75;
};

export const PlayersTable = ({ participants, gameStatus }: Props) => {
    const sortedParticipants = participants?.sort((a, b) => b.balance - a.balance) || [];
    const scaleFactor = getScaleFactor(sortedParticipants.length);

    const showWagers =
        gameStatus === GameStatuses.makingWagers ||
        gameStatus === GameStatuses.displayEliminated ||
        gameStatus === GameStatuses.answering ||
        gameStatus === GameStatuses.checkingAnswers;

    const showAnswers = gameStatus === GameStatuses.answering || gameStatus === GameStatuses.checkingAnswers;
    const isCheckingAnswers = gameStatus === GameStatuses.checkingAnswers;

    const getPlaceSuffix = (place: number): string => {
        if (place === 1) return 'st';
        if (place === 2) return 'nd';
        if (place === 3) return 'rd';
        return 'th';
    };

    return (
        <TableContainer scaleFactor={scaleFactor}>
            <table>
                <tbody>
                    {/* Place Row */}
                    <tr className="placeRow">
                        <th className="emptyHeader"></th>
                        {sortedParticipants.map((_, index) => (
                            <th key={index} className="placeCell">
                                {index + 1}
                                <sup>{getPlaceSuffix(index + 1)}</sup>
                            </th>
                        ))}
                    </tr>

                    {/* Name Row */}
                    <tr className="nameRow">
                        <th className="rowLabel">Name</th>
                        {sortedParticipants.map((participant, index) => (
                            <td key={index} className="nameCell">
                                {participant.name}
                            </td>
                        ))}
                    </tr>

                    {/* Bank Row */}
                    <tr className="bankRow">
                        <th className="rowLabel">Bank</th>
                        {sortedParticipants.map((participant, index) => (
                            <td key={index} className="bankCell">
                                ${ToDisplayNumber(participant.balance)}
                            </td>
                        ))}
                    </tr>

                    {/* Wager Row */}
                    <tr className="wagerRow">
                        <th className="rowLabel">Wager</th>
                        {sortedParticipants.map((participant, index) => (
                            <td key={index} className="wagerCell">
                                {showWagers && participant.wager !== undefined && participant.wager >= 0
                                    ? `$${ToDisplayNumber(participant.wager)}`
                                    : ''}
                            </td>
                        ))}
                    </tr>

                    {/* Answer Row (shown during answering/checking states) */}
                    {showAnswers && (
                        <tr className="answerRow">
                            <th className="rowLabel">Answer</th>
                            {sortedParticipants.map((participant, index) => {
                                const isCorrect = participant.isCorrectAnswer;
                                const isWrong = isCheckingAnswers && participant.answerCode && !isCorrect;
                                return (
                                    <td
                                        key={index}
                                        className={`answerCell${isWrong ? ' wrong' : ''}${
                                            isCorrect && isCheckingAnswers ? ' correct' : ''
                                        }`}
                                    >
                                        {participant.answerCode || ''}
                                    </td>
                                );
                            })}
                        </tr>
                    )}
                </tbody>
            </table>
        </TableContainer>
    );
};
